import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, IMAGES } from "@/lib/api";
import { toast } from "sonner";

export default function Auth() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(mode === "register" ? `Welcome, Investigator ${data.investigator_number}.` : "Access granted.");
      navigate("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Access denied.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-md px-4 py-3 text-[#FDF6E3] outline-none transition-colors";
  const fieldStyle = { background: "var(--wood-dark)", border: "1px solid var(--wood-medium)" };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--deep-shadow)" }} data-testid="auth-page">
      <div className="relative hidden lg:block">
        <img src={IMAGES.entrance} alt="Headquarters entrance" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,7,5,0.2), rgba(10,7,5,0.9))" }} />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-serif-hq italic text-2xl text-[#E6D5B8]">"Your mind becomes what you consume."</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lamp-glow">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <button onClick={() => navigate("/")} data-testid="auth-back-btn"
            className="flex items-center gap-2 font-typewriter text-xs uppercase tracking-widest text-[#A89A80] hover:text-[#F59E0B] transition-colors mb-8">
            <ArrowLeft size={15} /> Back to Headquarters
          </button>

          <p className="font-typewriter text-[11px] uppercase tracking-[0.35em] text-[#D97706]">
            {mode === "register" ? "New Recruit" : "Returning Investigator"}
          </p>
          <h1 className="font-serif-hq text-4xl text-[#FDF6E3] mt-2 mb-8">
            {mode === "register" ? "Join the Investigation" : "Sign In to HQ"}
          </h1>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input data-testid="auth-name-input" required placeholder="Investigator name"
                className={field} style={fieldStyle} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            )}
            <input data-testid="auth-email-input" required type="email" placeholder="Email"
              className={field} style={fieldStyle} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="auth-password-input" required type="password" placeholder="Password"
              className={field} style={fieldStyle} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} />

            {error && <p data-testid="auth-error" className="font-typewriter text-xs text-[#F87171]">{error}</p>}

            <button data-testid="auth-submit-btn" type="submit" disabled={loading}
              className="w-full rounded-md py-3 font-typewriter text-sm uppercase tracking-[0.25em] transition-colors disabled:opacity-60"
              style={{ background: "#991B1B", color: "#FDF6E3" }}>
              {loading ? "Verifying…" : mode === "register" ? "Enroll" : "Enter"}
            </button>
          </form>

          <button data-testid="auth-toggle-btn"
            onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); }}
            className="mt-6 font-typewriter text-xs uppercase tracking-widest text-[#A89A80] hover:text-[#F59E0B] transition-colors">
            {mode === "register" ? "Already enrolled? Sign in →" : "New here? Enroll as investigator →"}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
